import React from 'react';
import {
  ComposedChart,
  Line,
  AreaChart,
  Area,
  BarChart,
  Bar,
  LineChart,
  PieChart,
  Pie,
  Cell,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer
} from 'recharts';

// Colores para los gráficos
const COLORS = {
  primary: '#3B82F6',
  success: '#10B981',
  warning: '#F59E0B',
  danger: '#EF4444',
  info: '#06B6D4',
  secondary: '#6B7280'
};

const PIE_COLORS = ['#3B82F6', '#10B981', '#F59E0B', '#EF4444', '#06B6D4', '#6B7280', '#8B5CF6'];

// Helper: formatear correctamente fechas 'YYYY-MM-DD' como fecha local (evita desfase de zona horaria)
const formatYMDLabelES = (s) => {
  try {
    if (!s) return '';
    const [y, m, d] = String(s).split('-').map(Number);
    if (!y || !m || !d) {
      return new Date(s).toLocaleDateString('es-CO', { month: 'short', day: 'numeric' });
    }
    const dt = new Date(y, m - 1, d);
    return dt.toLocaleDateString('es-CO', { month: 'short', day: 'numeric' });
  } catch {
    return String(s);
  }
};

// Helper: obtener 'YYYY-MM-DD' desde un Date en hora local
const toYMD = (dt) => {
  const y = dt.getFullYear();
  const m = String(dt.getMonth() + 1).padStart(2, '0');
  const d = String(dt.getDate()).padStart(2, '0');
  return `${y}-${m}-${d}`;
};

// Componente para gráfico de líneas - Evolución de pedidos
export const OrderEvolutionChart = (props) => {
  const { data, loading } = props;
  if (loading) {
    return (
      <div className="h-80 flex items-center justify-center">
        <div className="animate-pulse text-gray-500">Cargando gráfico...</div>
      </div>
    );
  }

  // Preprocesar y ordenar datos
  const items = Array.isArray(data) ? data : [];

  // Detectar si debemos agrupar por MES
  // Si filterType es 'year' o filterPeriod es 'year', mostramos resumen mensual
  const isMonthlyView = props.filterType === 'year' || props.filterPeriod === 'year';

  const normalizeToYMD = (v) => {
    if (!v) return null;
    const s = String(v);
    const ymd = s.includes('T') ? s.split('T')[0] : (s.includes(' ') ? s.split(' ')[0] : s);
    if (/^\d{4}-\d{2}-\d{2}$/.test(ymd)) return ymd;
    const d = new Date(s);
    if (isNaN(d)) return null;
    return toYMD(d);
  };

  let formattedData = [];

  if (isMonthlyView) {
    // AGRUPACIÓN MENSUAL
    const monthMap = new Map(); // key: "YYYY-MM" -> { count, revenue, dateRaw }

    items.forEach(item => {
      const ymd = normalizeToYMD(item.date);
      if (!ymd) return;
      const monthKey = ymd.slice(0, 7); // "YYYY-MM"

      const current = monthMap.get(monthKey) || { count: 0, revenue: 0, dateRaw: monthKey + '-01' };
      monthMap.set(monthKey, {
        count: current.count + Number(item.count || 0),
        revenue: current.revenue + Number(item.revenue || 0),
        dateRaw: current.dateRaw
      });
    });

    formattedData = Array.from(monthMap.values()).map(entry => {
      // Formatear etiqueta de mes: "Ene", "Feb", etc.
      const [y, m] = entry.dateRaw.split('-');
      const dateObj = new Date(Number(y), Number(m) - 1, 1);
      const label = dateObj.toLocaleDateString('es-CO', { month: 'short', year: 'numeric' }); // "ene. 2023"

      return {
        dateRaw: entry.dateRaw,
        date: label, // Etiqueta para el eje X
        count: entry.count,
        revenue: entry.revenue
      };
    });

  } else {
    // VISTA DIARIA (Día a día) - Lógica original limpia
    formattedData = items
      .map(item => {
        const key = normalizeToYMD(item.date);
        if (!key) return null;
        return {
          dateRaw: key, // Para ordenar
          date: formatYMDLabelES(key),
          count: Number(item.count || 0),
          revenue: Number(item.revenue || 0)
        };
      })
      .filter(Boolean);
  }

  // Ordenar cronológicamente
  formattedData.sort((a, b) => a.dateRaw.localeCompare(b.dateRaw));

  return (
    <ResponsiveContainer width="100%" height={320}>
      <ComposedChart data={formattedData}>
        <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
        <XAxis
          dataKey="date"
          stroke="#6B7280"
          fontSize={12}
        />
        {/* Eje izquierdo para Ingresos (Moneda) - BARRAS */}
        <YAxis
          yAxisId="left"
          stroke={COLORS.success}
          fontSize={12}
          tickFormatter={(value) => `$${(value / 1000000).toFixed(1)}M`}
        />
        {/* Eje derecho para Pedidos (Cantidad) - LÍNEA */}
        <YAxis
          yAxisId="right"
          orientation="right"
          stroke={COLORS.primary}
          fontSize={12}
        />
        <Tooltip
          contentStyle={{
            backgroundColor: '#fff',
            border: '1px solid #e5e7eb',
            borderRadius: '8px',
            boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1)'
          }}
          formatter={(value, name) => {
            if (name === 'Ingresos ($)') return [`$${new Intl.NumberFormat('es-CO').format(value)}`, name];
            return [value, name];
          }}
        />
        <Legend />

        {/* BARRAS para el dinero (Ingresos) - Eje Izquierdo */}
        <Bar
          yAxisId="left"
          dataKey="revenue"
          name="Ingresos ($)"
          fill={COLORS.success}
          fillOpacity={0.6}
          radius={[4, 4, 0, 0]}
          barSize={isMonthlyView ? 40 : undefined}
        />

        {/* LÍNEA para la cantidad (Pedidos) - Eje Derecho */}
        <Line
          yAxisId="right"
          type="monotone"
          dataKey="count"
          stroke={COLORS.primary}
          strokeWidth={3}
          dot={{ fill: COLORS.primary, strokeWidth: 2, r: 4 }}
          name="Pedidos"
        />
      </ComposedChart>
    </ResponsiveContainer>
  );
};

// Componente para gráfico de barras - Métodos de entrega
export const DeliveryMethodChart = ({ data, loading }) => {
  if (loading) {
    return (
      <div className="h-80 flex items-center justify-center">
        <div className="animate-pulse text-gray-500">Cargando gráfico...</div>
      </div>
    );
  }

  const methodLabels = {
    domicilio: 'Domicilio',
    recogida_tienda: 'Recogida en Tienda',
    envio_nacional: 'Envío Nacional',
    envio_internacional: 'Envío Internacional'
  };

  // Agrupar por etiqueta de visualización para evitar duplicados en el eje X
  const aggregatedMap = new Map();

  (data || []).forEach(item => {
    const rawMethod = String(item.method || '').toLowerCase().trim();
    const label = methodLabels[rawMethod] || (rawMethod ? rawMethod : 'Domicilio');

    if (aggregatedMap.has(label)) {
      const existing = aggregatedMap.get(label);
      existing.count += parseInt(item.count || 0);
      existing.total_amount += parseFloat(item.total_amount || 0);
    } else {
      aggregatedMap.set(label, {
        method: label,
        count: parseInt(item.count || 0),
        total_amount: parseFloat(item.total_amount || 0)
      });
    }
  });

  const formattedData = Array.from(aggregatedMap.values());

  return (
    <ResponsiveContainer width="100%" height={320}>
      <BarChart data={formattedData}>
        <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
        <XAxis
          dataKey="method"
          stroke="#6B7280"
          fontSize={12}
          angle={-45}
          textAnchor="end"
          height={80}
        />
        <YAxis stroke="#6B7280" fontSize={12} />
        <Tooltip
          contentStyle={{
            backgroundColor: '#fff',
            border: '1px solid #e5e7eb',
            borderRadius: '8px',
            boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1)'
          }}
        />
        <Legend />
        <Bar
          dataKey="count"
          fill={COLORS.primary}
          name="Cantidad"
          radius={[4, 4, 0, 0]}
        />
      </BarChart>
    </ResponsiveContainer>
  );
};

// Componente para gráfico circular - Estados de pedidos
export const OrderStatusChart = ({ data, loading }) => {
  if (loading) {
    return (
      <div className="h-80 flex items-center justify-center">
        <div className="animate-pulse text-gray-500">Cargando gráfico...</div>
      </div>
    );
  }

  const statusLabels = {
    pendiente: 'Pendiente',
    confirmado: 'Confirmado',
    en_preparacion: 'En Preparación',
    listo: 'Listo',
    enviado: 'Enviado',
    entregado: 'Entregado',
    cancelado: 'Cancelado'
  };

  const formattedData = data?.map((item, index) => ({
    ...item,
    name: statusLabels[item.status] || item.status,
    value: parseInt(item.count),
    color: PIE_COLORS[index % PIE_COLORS.length]
  })) || [];

  const renderCustomLabel = ({ cx, cy, midAngle, innerRadius, outerRadius, percent }) => {
    if (percent < 0.05) return null; // No mostrar etiquetas para segmentos muy pequeños

    const RADIAN = Math.PI / 180;
    const radius = innerRadius + (outerRadius - innerRadius) * 0.5;
    const x = cx + radius * Math.cos(-midAngle * RADIAN);
    const y = cy + radius * Math.sin(-midAngle * RADIAN);

    return (
      <text
        x={x}
        y={y}
        fill="white"
        textAnchor={x > cx ? 'start' : 'end'}
        dominantBaseline="central"
        fontSize={12}
        fontWeight="bold"
      >
        {`${(percent * 100).toFixed(0)}%`}
      </text>
    );
  };

  return (
    <ResponsiveContainer width="100%" height={320}>
      <PieChart>
        <Pie
          data={formattedData}
          cx="50%"
          cy="50%"
          labelLine={false}
          label={renderCustomLabel}
          outerRadius={100}
          fill="#8884d8"
          dataKey="value"
        >
          {formattedData.map((entry, index) => (
            <Cell key={`cell-${index}`} fill={entry.color} />
          ))}
        </Pie>
        <Tooltip
          contentStyle={{
            backgroundColor: '#fff',
            border: '1px solid #e5e7eb',
            borderRadius: '8px',
            boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1)'
          }}
        />
        <Legend />
      </PieChart>
    </ResponsiveContainer>
  );
};

// Componente para gráfico de área - Ingresos acumulados
export const RevenueAreaChart = ({ data, loading }) => {
  if (loading) {
    return (
      <div className="h-80 flex items-center justify-center">
        <div className="animate-pulse text-gray-500">Cargando gráfico...</div>
      </div>
    );
  }

  const formattedData = data?.map(item => ({
    ...item,
    week: `Sem ${item.week}`,
    revenue: parseFloat(item.revenue || 0)
  })) || [];

  return (
    <ResponsiveContainer width="100%" height={320}>
      <AreaChart data={formattedData}>
        <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
        <XAxis
          dataKey="week"
          stroke="#6B7280"
          fontSize={12}
        />
        <YAxis stroke="#6B7280" fontSize={12} />
        <Tooltip
          contentStyle={{
            backgroundColor: '#fff',
            border: '1px solid #e5e7eb',
            borderRadius: '8px',
            boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1)'
          }}
          formatter={(value) => [`$${value.toLocaleString()}`, 'Ingresos']}
        />
        <Area
          type="monotone"
          dataKey="revenue"
          stroke={COLORS.success}
          fill={COLORS.success}
          fillOpacity={0.3}
          strokeWidth={2}
        />
      </AreaChart>
    </ResponsiveContainer>
  );
};

/**
 * Gráfico de líneas múltiples para tendencias del mensajero
 * Series: assigned, accepted, in_delivery, delivered, failed
 */
export const MessengerTrendsChart = ({ data, loading }) => {
  if (loading) {
    return (
      <div className="h-80 flex items-center justify-center">
        <div className="animate-pulse text-gray-500">Cargando gráfico...</div>
      </div>
    );
  }

  const formattedData = (data || []).map(item => ({
    ...item,
    date: formatYMDLabelES(item.date),
    assigned: Number(item.assigned || 0),
    accepted: Number(item.accepted || 0),
    in_delivery: Number(item.in_delivery || 0),
    delivered: Number(item.delivered || 0),
    failed: Number(item.failed || 0),
  }));

  return (
    <ResponsiveContainer width="100%" height={320}>
      <LineChart data={formattedData}>
        <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
        <XAxis dataKey="date" stroke="#6B7280" fontSize={12} />
        <YAxis stroke="#6B7280" fontSize={12} />
        <Tooltip
          contentStyle={{
            backgroundColor: '#fff',
            border: '1px solid #e5e7eb',
            borderRadius: '8px',
            boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1)'
          }}
        />
        <Legend />
        <Line type="monotone" dataKey="assigned" name="Asignados" stroke={COLORS.info} strokeWidth={2} dot={false} />
        <Line type="monotone" dataKey="accepted" name="Aceptados" stroke={COLORS.secondary} strokeWidth={2} dot={false} />
        <Line type="monotone" dataKey="in_delivery" name="En ruta" stroke={COLORS.warning} strokeWidth={2} dot={false} />
        <Line type="monotone" dataKey="delivered" name="Entregados" stroke={COLORS.success} strokeWidth={3} dot={{ r: 2 }} />
        <Line type="monotone" dataKey="failed" name="Fallidos" stroke={COLORS.danger} strokeWidth={2} dot={false} />
      </LineChart>
    </ResponsiveContainer>
  );
};

/**
 * Gráfico de barras por método de entrega del mensajero
 */
export const MessengerByMethodChart = ({ data, loading }) => {
  if (loading) {
    return (
      <div className="h-80 flex items-center justify-center">
        <div className="animate-pulse text-gray-500">Cargando gráfico...</div>
      </div>
    );
  }

  const methodLabels = {
    mensajeria_urbana: 'Mensajería urbana',
    mensajeria_local: 'Mensajería local',
    domicilio: 'Domicilio'
  };

  const formattedData = (data || []).map(item => ({
    method: methodLabels[item.delivery_method] || item.delivery_method || 'Otro',
    count: parseInt(item.count || 0),
    total_amount: parseFloat(item.total_amount || 0)
  }));

  return (
    <ResponsiveContainer width="100%" height={320}>
      <BarChart data={formattedData}>
        <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
        <XAxis
          dataKey="method"
          stroke="#6B7280"
          fontSize={12}
          angle={-15}
          textAnchor="end"
          height={50}
        />
        <YAxis stroke="#6B7280" fontSize={12} />
        <Tooltip
          contentStyle={{
            backgroundColor: '#fff',
            border: '1px solid #e5e7eb',
            borderRadius: '8px',
            boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1)'
          }}
        />
        <Legend />
        <Bar dataKey="count" name="Entregas" fill={COLORS.primary} radius={[4, 4, 0, 0]} />
      </BarChart>
    </ResponsiveContainer>
  );
};

/**
 * Gráfico de barras por hora de entrega del mensajero
 */
export const MessengerByHourChart = ({ data, loading }) => {
  if (loading) {
    return (
      <div className="h-80 flex items-center justify-center">
        <div className="animate-pulse text-gray-500">Cargando gráfico...</div>
      </div>
    );
  }

  const formattedData = (data || []).map(item => ({
    hour: `${String(item.hour).padStart(2, '0')}:00`,
    delivered: parseInt(item.delivered || 0)
  }));

  return (
    <ResponsiveContainer width="100%" height={320}>
      <BarChart data={formattedData}>
        <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
        <XAxis dataKey="hour" stroke="#6B7280" fontSize={12} />
        <YAxis stroke="#6B7280" fontSize={12} />
        <Tooltip
          contentStyle={{
            backgroundColor: '#fff',
            border: '1px solid #e5e7eb',
            borderRadius: '8px',
            boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1)'
          }}
        />
        <Legend />
        <Bar dataKey="delivered" name="Entregas" fill={COLORS.success} radius={[4, 4, 0, 0]} />
      </BarChart>
    </ResponsiveContainer>
  );
};

export default {
  OrderEvolutionChart,
  DeliveryMethodChart,
  OrderStatusChart,
  RevenueAreaChart,
  MessengerTrendsChart,
  MessengerByMethodChart,
  MessengerByHourChart
};
